import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import MagneticButton from "./MagneticButton";

export default function Contact() {
    const compRef = useRef(null);
    const [formData, setFormData] = useState({ name: "", phone: "", time: "" });
    const [status, setStatus] = useState("idle"); // 'idle' | 'loading' | 'success' | 'error'

    useEffect(() => {
        let ctx = gsap.context(() => {
            gsap.from(".contact-el", {
                scrollTrigger: {
                    trigger: compRef.current,
                    start: "top 80%",
                },
                y: 30,
                opacity: 0,
                stagger: 0.15,
                duration: 1,
                ease: "power3.out",
            });
        }, compRef);
        return () => ctx.revert();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.phone) {
            alert("Wypełnij imię oraz numer telefonu.");
            return;
        }

        setStatus("loading");

        // Connected to Google Sheets Webhook
        const scriptURL = 'https://script.google.com/macros/s/AKfycbyoDdavxXZzdT8-vuXHyELp-zIH6H1Rw7MFRkVuSkJRNnKDQaQpuC3RN1y2vCe8n70L/exec'; 

        const data = new FormData();
        data.append('Imię/Firma', formData.name);
        data.append('Telefon', formData.phone);
        data.append('Preferowane godziny', formData.time || "Nie podano");

        try {
            const response = await fetch(scriptURL, { 
                method: 'POST', 
                body: data,
                mode: 'no-cors' // Google Scripts requires no-cors from browser, response is opaque
            });

            // Since mode is no-cors, we assume success if fetch didn't throw an error network-wise
            setStatus("success");
            setFormData({ name: "", phone: "", time: "" });

            setTimeout(() => {
                setStatus("idle");
            }, 5000); // Reset after 5 seconds
        } catch (error) {
            console.error('Error!', error.message);
            setStatus("error");
            alert("Wystąpił błąd podczas wysyłania. Spróbuj zadzwonić na nasz numer.");
            setStatus("idle");
        }
    };

    return (
        <section ref={compRef} id="kontakt" className="bg-primary py-24 px-6 md:px-16 text-dark border-t border-dark/10">
            <div className="max-w-7xl mx-auto">

                <div className="mb-16">
                    <h2 className="contact-el text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-4 text-dark">
                        Gotowy na wymianę?
                    </h2>
                    <p className="contact-el font-body text-xl text-dark/70 max-w-2xl">
                        Działamy w Mysłowicach i całym województwie śląskim. Zostaw numer - oddzwonimy i ustalimy termin bezpłatnej wizji lokalnej.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">

                    {/* Form Section */}
                    <div className="contact-el">
                        <form className="space-y-6 flex flex-col" onSubmit={handleSubmit}>
                            <div className="flex items-start gap-4 mb-4 bg-accent/5 p-6 rounded-3xl border border-accent/10 shadow-inner">
                                <div className="w-2.5 h-2.5 rounded-full bg-accent animate-pulse mt-1.5 shrink-0"></div>
                                <span className="font-body text-sm md:text-base text-dark/80 leading-relaxed">
                                    Ze względu na trwający sezon, w tym tygodniu możemy przyjąć jeszcze tylko <span className="font-bold text-accent">2 zlecenia</span> na bezpłatny pomiar na terenie Śląska.
                                </span>
                            </div>

                            <div className="relative group">
                                <input
                                    type="text"
                                    id="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="peer w-full bg-background border border-dark/10 rounded-2xl px-6 pt-8 pb-3 text-dark font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/50 hover:border-accent/50 hover:shadow-[0_0_15px_rgba(230,59,46,0.1)] transition-all"
                                    placeholder=" "
                                    required
                                    disabled={status === 'loading' || status === 'success'}
                                />
                                <label
                                    htmlFor="name"
                                    className={`absolute left-6 top-4 font-mono text-[10px] uppercase tracking-widest text-dark/40 transition-all pointer-events-none font-bold
                                        ${formData.name ? "top-3 text-[10px]" : "peer-placeholder-shown:text-xs peer-placeholder-shown:top-6"}
                                        peer-focus:top-3 peer-focus:text-[10px] peer-focus:text-accent`}
                                >
                                    Imię i nazwisko / Firma
                                </label>
                            </div>

                            <div className="relative group">
                                <input
                                    type="tel"
                                    id="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="peer w-full bg-background border border-dark/10 rounded-2xl px-6 pt-8 pb-3 text-dark font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/50 hover:border-accent/50 hover:shadow-[0_0_15px_rgba(230,59,46,0.1)] transition-all"
                                    placeholder=" "
                                    required
                                    disabled={status === 'loading' || status === 'success'}
                                />
                                <label
                                    htmlFor="phone"
                                    className={`absolute left-6 top-4 font-mono text-[10px] uppercase tracking-widest text-dark/40 transition-all pointer-events-none font-bold
                                        ${formData.phone ? "top-3 text-[10px]" : "peer-placeholder-shown:text-xs peer-placeholder-shown:top-6"}
                                        peer-focus:top-3 peer-focus:text-[10px] peer-focus:text-accent`}
                                >
                                    Numer Telefonu
                                </label>
                            </div>

                            <div className="relative group">
                                <select
                                    id="time"
                                    value={formData.time}
                                    onChange={handleChange}
                                    className="peer w-full bg-background border border-dark/10 rounded-2xl px-6 pt-8 pb-3 text-dark font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/50 hover:border-accent/50 hover:shadow-[0_0_15px_rgba(230,59,46,0.1)] transition-all appearance-none cursor-pointer"
                                    disabled={status === 'loading' || status === 'success'}
                                >
                                    <option value="" disabled hidden></option>
                                    <option value="Poranek (8:00 - 12:00)">Poranek (8:00 - 12:00)</option>
                                    <option value="Popołudnie (12:00 - 16:00)">Popołudnie (12:00 - 16:00)</option>
                                    <option value="Wieczór (16:00 - 19:00)">Wieczór (16:00 - 19:00)</option>
                                    <option value="DOWOLNE (dzwońcie od razu)">DOWOLNE (dzwońcie od razu)</option>
                                </select>
                                <label
                                    htmlFor="time"
                                    className={`absolute left-6 top-4 font-mono text-[10px] uppercase tracking-widest text-dark/40 transition-all pointer-events-none font-bold
                                        ${formData.time ? "top-3 text-[10px]" : "top-6 text-xs"}
                                        peer-focus:top-3 peer-focus:text-[10px] peer-focus:text-accent`}
                                >
                                    Preferowane godziny kontaktu
                                </label>
                                {/* Custom select arrow */}
                                <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-dark/30">
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            </div>

                            <MagneticButton
                                type="submit"
                                disabled={status === 'loading' || status === 'success'}
                                className={`mt-6 w-full font-heading font-bold text-lg md:text-xl px-8 py-6 rounded-full uppercase tracking-widest flex items-center justify-center gap-3 transition-all duration-300
                                    ${status === 'success' ? 'bg-[#4ADE80] text-dark shadow-[0_10px_40px_rgba(74,222,128,0.3)]' : 'bg-accent text-background hover:bg-accent/90 shadow-[0_10px_40px_rgba(204,88,51,0.3)]'}
                                    ${status === 'loading' ? 'opacity-80 cursor-wait' : ''}
                                `}
                            >
                                {status === 'idle' && <>Zamów Wycenę <ArrowUpRight size={24} /></>}
                                {status === 'loading' && <><Loader2 size={24} className="animate-spin" /> Wysyłanie...</>}
                                {status === 'success' && <><CheckCircle2 size={24} /> Otrzymano Zgłoszenie</>}
                            </MagneticButton>
                            <p className="text-center font-mono text-[10px] text-dark/40 mt-6 uppercase tracking-widest px-4 leading-relaxed">
                                Wysyłka formularza nie zobowiązuje do zakupu. <br className="hidden sm:block" /> Twoje dane są bezpieczne i trafią wprost do Arkusza.
                            </p>
                        </form>
                    </div>

                    {/* NAP & Info */}
                    <div className="contact-el flex flex-col justify-between bg-primary text-dark p-10 md:p-14 rounded-[3rem] border border-dark/10 shadow-sm">
                        <div>
                            <span className="font-mono text-sm text-accent uppercase tracking-widest block mb-4">
                                Zadzwoń Bezpośrednio
                            </span>
                            <a href="tel:+48786148469" className="inline-block hover:text-accent transition-colors font-heading font-bold">
                                <span className="text-2xl md:text-3xl tracking-widest">+48 786 148 469</span>
                            </a>
                        </div>

                        <div className="w-full text-left font-mono text-sm leading-relaxed border-t border-dark/10 pt-8 mt-auto">
                            <strong className="block text-accent mb-2 uppercase tracking-widest font-heading text-lg">TOBMAR Mariusz Kołodziej</strong>
                            <span className="block opacity-80">Usługi Dekarskie</span>
                            <span className="block opacity-80 mt-4">Mysłowice i okolice (Woj. Śląskie)</span>
                            <span className="block opacity-80 mt-4 mb-4">Fixly: 4.6/5 (14 opinii)</span>
                            <div className="w-full h-32 rounded-xl overflow-hidden border border-dark/10 filter grayscale contrast-125 opacity-80">
                                <iframe
                                    title="Mapa Mysłowice"
                                    src="https://maps.google.com/maps?q=Mysłowice&t=&z=12&ie=UTF8&iwloc=&output=embed"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                ></iframe>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </section>
    );
}
